import React, { Fragment, useEffect, useMemo, useState } from "react";
import { Link, useHistory, useLocation } from "react-router-dom";
import * as EmailValidator from "email-validator";
import { useDispatch, useSelector } from "react-redux";
import {
  userSigninAction,
  clearErrorAction,
  userLoadingAction,
} from "../../redux/actions/userAction";
import swal from "sweetalert";
import MetaTag from "../../layout/MetaTag";
import { useAlert } from "react-alert";
import { Helmet } from "react-helmet";
import axios from "axios";
import "./signin.css";
import Navbar from "../HomeScreen/Navbar";
import Footer from "../HomeScreen/bottomsection/Footer";
import Loader from "../../layout/loader";
import PersonIcon from "@mui/icons-material/Person";
import EmailIcon from "@mui/icons-material/Email";
import LocalPhoneIcon from "@mui/icons-material/LocalPhone";
import LockIcon from "@mui/icons-material/Lock";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";

function splitName(fullName) {
  const parts = fullName.trim().split(/\s+/).filter(Boolean);
  const firstName = parts.shift() || "";
  const lastName = parts.length > 0 ? parts.join(" ") : "Customer";

  return { firstName, lastName };
}

function getSignupApiError(error, fallback) {
  const serverMessage = error.response?.data?.msg || error.response?.data?.message;

  if (serverMessage) return serverMessage;

  if (error.response?.status === 500) {
    return "Signup server is not responding correctly. Please restart the backend server and try again.";
  }

  if (!error.response) {
    return "Cannot reach the backend server. Please make sure the backend is running on port 5000.";
  }

  return error.message || fallback;
}

function Signinpage() {
  const location = useLocation();
  const history = useHistory();
  const dispatch = useDispatch();
  const alert = useAlert();

  const { error, loading, isAuthenticated } = useSelector((state) => state.user);

  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [number, setNumber] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [terms, setTerms] = useState(true);
  const [otpSent, setOtpSent] = useState(false);
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);

  const maskedNumber = useMemo(() => {
    if (number.length !== 10) return number;
    return `${number.slice(0, 2)}******${number.slice(-2)}`;
  }, [number]);

  const maskedEmail = useMemo(() => {
    const cleanEmail = email.trim();
    const [name, domain] = cleanEmail.split("@");
    if (!name || !domain) return cleanEmail;
    return `${name.slice(0, 2)}${name.length > 2 ? "***" : ""}@${domain}`;
  }, [email]);

  const validateSignupForm = () => {
    if (!fullName.trim() || !email.trim() || !number || !password || !confirmPassword) {
      swal({
        title: "Validation Error",
        text: "Please fill all required details.",
        icon: "info",
      });
      return false;
    }

    if (!EmailValidator.validate(email.trim())) {
      swal({
        title: "Validation Error",
        text: "Please enter a valid email address.",
        icon: "info",
      });
      return false;
    }

    if (!/^\d{10}$/.test(number)) {
      swal({
        title: "Validation Error",
        text: "Please enter a valid 10 digit mobile number.",
        icon: "info",
      });
      return false;
    }

    if (password.length < 6) {
      swal({
        title: "Validation Error",
        text: "Password should be at least 6 characters.",
        icon: "info",
      });
      return false;
    }

    if (password !== confirmPassword) {
      swal({
        title: "Validation Error",
        text: "Password and confirm password should be same.",
        icon: "info",
      });
      return false;
    }

    if (!terms) {
      swal({
        title: "Validation Error",
        text: "Please accept terms and privacy policy to continue.",
        icon: "info",
      });
      return false;
    }

    return true;
  };

  const sendOtpHandler = async (e) => {
    e.preventDefault();
    if (!validateSignupForm()) return;

    try {
      setIsSendingOtp(true);
      const { data } = await axios.post(`/api/verfiy-number`, {
        email: email.trim(),
        number,
      });

      setOtpSent(true);
      swal({
        title: "OTP Sent",
        text: data.message || "Verification code sent to your email.",
        icon: "success",
      });
    } catch (requestError) {
      swal({
        title: "OTP Error",
        text: getSignupApiError(requestError, "Unable to send verification code."),
        icon: "error",
      });
    } finally {
      setIsSendingOtp(false);
    }
  };

  const resendOtpHandler = async () => {
    if (!validateSignupForm()) return;

    try {
      setIsSendingOtp(true);
      const { data } = await axios.post(`/api/verfiy-number`, {
        email: email.trim(),
        number,
      });

      swal({
        title: "OTP Re-sent",
        text: data.message || "A fresh verification code has been sent to your email.",
        icon: "success",
      });
    } catch (requestError) {
      swal({
        title: "OTP Error",
        text: getSignupApiError(requestError, "Unable to resend verification code."),
        icon: "error",
      });
    } finally {
      setIsSendingOtp(false);
    }
  };

  const submitHandlerForSign = async (e) => {
    e.preventDefault();
    if (!validateSignupForm()) return;

    if (!/^\d{4,8}$/.test(otp.trim())) {
      return swal({
        title: "Validation Error",
        text: "Please enter the OTP sent to your email.",
        icon: "info",
      });
    }

    try {
      setIsVerifyingOtp(true);
      const { data } = await axios.post(`/api/verfiy-otp`, {
        email: email.trim(),
        number,
        otp,
      });

      if (data.smsResult?.valid !== true) {
        return swal({
          title: "OTP Verification",
          text: "Invalid OTP. Please check and try again.",
          icon: "error",
        });
      }

      const { firstName, lastName } = splitName(fullName);
      dispatch(
        userSigninAction(
          firstName,
          lastName,
          email.trim(),
          number,
          password,
          confirmPassword,
          "not specified"
        )
      );
    } catch (requestError) {
      swal({
        title: "OTP Verification",
        text: getSignupApiError(requestError, "Unable to verify OTP."),
        icon: "error",
      });
    } finally {
      setIsVerifyingOtp(false);
    }
  };

  useEffect(() => {
    if (error) {
      alert.error(error);
      dispatch(clearErrorAction());
    }
    if (isAuthenticated) {
      history.push("/userprofile");
      dispatch(userLoadingAction());
    }
  }, [alert, dispatch, error, history, isAuthenticated, location.pathname]);

  return (
    <Fragment>
      <Helmet>
        <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=2.0" />
        <meta name="description" content="Create your IM Online Shopping account." />
        <meta name="robots" content="index, follow" />
      </Helmet>
      <MetaTag title="Create your IM Online Shopping account" />
      {loading ? (
        <Loader />
      ) : (
        <Fragment>
          <Navbar />
          <main className="signup-page-shell">
            <section className="signup-brand-panel" aria-label="Signup benefits">
              <span className="signup-eyebrow">IM Online Shopping</span>
              <h1>Create your shopping account</h1>
              <p>
                Save addresses, track orders, manage returns, and keep your wishlist ready for
                the next drop.
              </p>
              <div className="signup-benefit-grid">
                <span>Fast checkout</span>
                <span>Order updates</span>
                <span>Wishlist sync</span>
              </div>
            </section>

            <section className="signup-card" aria-label="Signup form">
              <div className="signup-card-heading">
                <h2>{otpSent ? "Verify your email" : "Sign up"}</h2>
                <p>
                  {otpSent
                    ? `Enter the OTP sent to ${maskedEmail || maskedNumber}.`
                    : "Only the essentials. No extra questions."}
                </p>
              </div>

              {!otpSent ? (
                <form method="POST" onSubmit={sendOtpHandler} className="signup-form">
                  <label className="signup-field">
                    <PersonIcon />
                    <input
                      type="text"
                      placeholder="Full name"
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      required
                    />
                  </label>

                  <label className="signup-field">
                    <EmailIcon />
                    <input
                      type="email"
                      placeholder="Email address"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                    />
                  </label>

                  <label className="signup-field">
                    <LocalPhoneIcon />
                    <input
                      type="tel"
                      placeholder="Mobile number"
                      maxLength="10"
                      inputMode="numeric"
                      pattern="[0-9]{10}"
                      value={number}
                      onChange={(e) => setNumber(e.target.value.replace(/\D/g, "").slice(0, 10))}
                      required
                    />
                  </label>

                  <label className="signup-field">
                    <LockIcon />
                    <input
                      type="password"
                      placeholder="Password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                    />
                  </label>

                  <label className="signup-field">
                    <LockIcon />
                    <input
                      type="password"
                      placeholder="Confirm password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                    />
                  </label>

                  <label className="signup-policy-row">
                    <input
                      checked={terms}
                      type="checkbox"
                      required
                      onChange={(e) => setTerms(e.target.checked)}
                    />
                    <span>
                      I agree to the <Link to="/terms-and-conditions">Terms of Use</Link> and{" "}
                      <Link to="/Cookies-Policy">Privacy Policy</Link>.
                    </span>
                  </label>

                  <button className="signup-primary-button" disabled={isSendingOtp}>
                    {isSendingOtp ? "Sending OTP..." : "Send OTP"}
                  </button>
                </form>
              ) : (
                <form method="POST" onSubmit={submitHandlerForSign} className="signup-form">
                  <label className="signup-field signup-otp-field">
                    <VerifiedUserIcon />
                    <input
                      type="text"
                      placeholder="Enter OTP"
                      maxLength="8"
                      inputMode="numeric"
                      value={otp}
                      onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 8))}
                      required
                    />
                  </label>

                  <button className="signup-primary-button" disabled={isVerifyingOtp}>
                    {isVerifyingOtp ? "Verifying..." : "Verify & Create Account"}
                  </button>

                  <div className="signup-secondary-actions">
                    <button type="button" onClick={() => setOtpSent(false)}>
                      Edit details
                    </button>
                    <button type="button" onClick={resendOtpHandler} disabled={isSendingOtp}>
                      {isSendingOtp ? "Sending..." : "Resend OTP"}
                    </button>
                  </div>
                </form>
              )}

              <p className="signup-login-link">
                Already have an account? <Link to="/Login">Login</Link>
              </p>
            </section>
          </main>
          <Footer />
        </Fragment>
      )}
    </Fragment>
  );
}

export default Signinpage;
